<template>
  <header class="site__header" :class="classes" v-if="show">
    <div class="site__header__img" v-if="showProfileImage">
      <img
        class="site__profile-img"
        :class="{
          'site__profile-img--has-border': campsite.site_profile_image_border,
        }"
        :src="profileImageUrl"
        alt="Profile picture"
        height="100"
        width="100"
        v-if="isEnabled"
      />
    </div>
    <div class="site__header__content">
      <h1 class="site__username" :class="{ 'sr-only': hideTitle }">
        {{ siteTitle }}
      </h1>
      <div class="site__location" v-if="campsite.site_location && isEnabled">
        <component
          :is="locationComponent"
          v-bind="locationAttrs"
          @click="handleLocationClick"
        >
          <MapMarkerIcon />
          {{ campsite.site_location }}
        </component>
      </div>
      <p class="site__intro" v-if="campsite.site_intro_text && isEnabled">
        {{ campsite.site_intro_text }}
      </p>

      <slot />
    </div>
  </header>
  <div v-else>
    <h1 class="sr-only">{{ siteTitle }}</h1>
    <slot />
  </div>
</template>

<script>
import {
  convertImageUrlToEncodedUrl,
  MapMarkerIcon,
  EVENTS,
} from '@campsite-bio/component-lib';
import { computed, useContext } from '@nuxtjs/composition-api';

export default {
  components: {
    MapMarkerIcon,
  },

  props: {
    isEnabled: Boolean,
    show: Boolean,
    hideShareButton: Boolean,
  },

  setup(props) {
    const { store, $trackEvent } = useContext();

    const isFreePlan = computed(() => store.getters.isFreePlan);
    const campsite = computed(() => store.state.campsite);

    const profileImageUrl = computed(() => {
      return convertImageUrlToEncodedUrl(campsite.value.profile_image, 200);
    });

    const siteTitle = computed(() => {
      if (!props.isEnabled) return 'This profile is coming soon...';
      if (campsite.value.site_intro_title)
        return campsite.value.site_intro_title;
      return `@${campsite.value.username}`;
    });

    const hideTitle = computed(() => {
      return campsite.value.site_hide_intro_title;
    });

    const showProfileImage = computed(() => {
      return !campsite.value.site_hide_profile_image;
    });

    const classes = computed(() => {
      return {
        'site__header--has-profile-image': showProfileImage.value,
        'site__header--no-share': props.hideShareButton,
        'site__header--align-left':
          campsite.value.site_header_layout === 'left-aligned',
      };
    });

    const locationComponent = computed(() => {
      if (!isFreePlan.value && campsite.value.site_location_url) {
        return 'a';
      }

      return 'span';
    });

    const locationAttrs = computed(() => {
      if (!isFreePlan.value && campsite.value.site_location_url) {
        return {
          href: campsite.value.site_location_url,
          target: '_blank',
          rel: 'noopener ugc',
        };
      }

      return {};
    });

    function handleLocationClick() {
      if (locationComponent.value !== 'a') return;

      $trackEvent(
        [
          {
            event_name: EVENTS.LOCATION,
            epoch: Date.now(),
          },
        ],
        {
          campsite_id: campsite.value._id.$oid,
        },
      );
    }

    return {
      campsite,
      profileImageUrl,
      siteTitle,
      hideTitle,
      showProfileImage,
      locationComponent,
      locationAttrs,
      handleLocationClick,
      classes,
    };
  },
};
</script>

<style lang="scss">
.site__header {
  text-align: center;
  padding-top: 3rem;
  position: relative;

  &--align-left {
    display: flex;
    gap: 1rem;
    padding-top: 2.5rem;
    text-align: left;
  }

  &--has-profile-image:not(.site__header--align-left),
  &--no-share {
    padding-top: 2rem;
  }
}

.site__header__img {
  flex-shrink: 0;
}

.site__profile-img {
  background: #fff;
  border: none;
  border-radius: 50%;
  height: 100px;
  margin-bottom: 0.325rem;
  width: 100px;
  object-fit: cover;

  .site__header--align-left & {
    height: 70px;
    width: 70px;
  }

  &--has-border {
    border: 0.125rem solid var(--p-image-border-color);
  }
}

.site__header__content {
  .site__header--align-left & {
    flex-grow: 1;
  }
}

.site__username,
.site__intro,
.site__location {
  margin: 0.325rem 0;
}

.site__username {
  font-size: 1.325rem;
  color: var(--p-title-color);
  font-weight: var(--p-title-font-weight);
  font-family: var(--p-title-font-family);
  line-height: 1.25;
  margin-bottom: 0.425rem;

  .site__header--align-left & {
    font-size: 1.125rem;
  }
}

.site__intro,
.site__location {
  font-weight: 400;
  font-size: 0.925rem;
  line-height: 1.6;
  margin-left: auto;
  margin-right: auto;
  max-width: 450px;

  .site__header--align-left & {
    margin-left: unset;
    margin-right: unset;
    font-size: 0.825rem;
  }
}

.site__location {
  font-size: 0.825rem;

  > * {
    align-items: center;
    color: inherit;
    display: inline-flex;
    gap: 0.25rem;
    text-decoration: none;
  }
}
</style>
